import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { type CrmOutreach } from '@/lib/supabase'
import { Mail, Phone, MessageSquare, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'

const TYPE_ICONS: Record<CrmOutreach['type'], React.ComponentType<{ className?: string }>> = {
  email: Mail,
  linkedin: MessageSquare,
  call: Phone,
  meeting: Calendar,
}

type OutreachTimelineProps = {
  leadId: string
  outreach: CrmOutreach[]
  onUpdated?: () => void
}

export function OutreachTimeline({ outreach }: OutreachTimelineProps) {
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Outreach ({outreach.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {outreach.length === 0 ? (
            <p className="text-sm text-muted-foreground">No touchpoints yet.</p>
          ) : (
            outreach.map((o) => {
              const Icon = TYPE_ICONS[o.type] ?? Mail
              return (
                <div
                  key={o.id}
                  className={cn(
                    'flex gap-3 rounded-lg border p-3 text-sm',
                    o.direction === 'inbound' ? 'border-l-4 border-l-primary' : ''
                  )}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium capitalize">{o.type}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground capitalize">{o.direction}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">{o.sent_by}</span>
                    </div>
                    {o.subject && (
                      <p className="mt-0.5 font-medium text-foreground">{o.subject}</p>
                    )}
                    {o.body && (
                      <p className="mt-1 line-clamp-2 text-muted-foreground">{o.body}</p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">{formatDate(o.created_at)}</p>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </CardContent>
    </Card>
  )
}
